import cx from 'clsx';
import * as React from 'react';
import { CategoryFinancialWebAttributes } from '../../../types/category/categoryTypes';

import { useWindowSize } from '../../../hooks/useWindowSize';
import { QuicksilverTileOutfitType } from '../../game-tile/index';
import ClosedOverlay from '../ClosedOverlay';
import { MEDIUM_BREAKPOINT, MEDIUM_OUTFIT } from '../constants';
import GhostTile from '../GhostTile';
import ModularGridGameTile from '../ModularGridGameTile';
import {
  GameClickProps,
  GameTileProps,
  GridSizeType,
  OverlayProps,
} from '../types';
import { sliceGameList, selectProperStyles } from '../utils';
import styles from './Grid.module.scss';

export interface GridProps {
  gameList?: GameTileProps[];
  onClick: (args: { gamePosition: number; game: GameClickProps }) => void;
  onInfoClick: (args: {
    gamePosition: number;
    game: GameClickProps;
    path: string;
  }) => void;
  itemsPerRow?: number;
  gamesLimit?: number;
  percentageWidth?: number;
  insideHeader?: boolean;
  overlay?: OverlayProps;
  ghostItems?: number;
  gameDetailsInfo?: boolean;
  gameInfoTooltip?: string;
  size?: GridSizeType;
  initialIndex?: number;
  type?: 'jackpotNine' | 'grid' | 'collection';
  categoryAttributes?: CategoryFinancialWebAttributes;
  renderExtendedInfo?: (game: GameTileProps) => JSX.Element | null;
}

const Grid: React.FC<GridProps> = ({
  gameList = [],
  onClick,
  onInfoClick,
  gamesLimit = 3,
  percentageWidth = 100,
  insideHeader = true,
  overlay,
  ghostItems,
  gameDetailsInfo,
  gameInfoTooltip,
  size,
  initialIndex = 0,
  type = 'grid',
  categoryAttributes,
  renderExtendedInfo,
}) => {
  const width = useWindowSize().width;
  if (!gameList.length && !overlay) return null;

  const isJackpotLarge =
    type === 'jackpotNine' &&
    (size === 'medium' || width >= MEDIUM_BREAKPOINT.min);

  const preparedGameList = sliceGameList(
    gamesLimit,
    isJackpotLarge ? gameList.slice(1) : gameList
  );
  const gamePosition = isJackpotLarge ? 2 : 1;

  const handleClick = (gamePosition: number, fn: Function) => (
    game: GameClickProps,
    path?: string
  ) => {
    fn({ game, gamePosition, ...(path && { path }) });
  };
  let slotPercentageSize = 0;

  if (percentageWidth !== 100) {
    slotPercentageSize = 100 / gameList.length;
  }
  const shouldWrap = !(percentageWidth < 100);
  const ghostItemsArray = Array.from(Array(ghostItems).keys());

  const containerClassName = cx(
    styles.container,
    insideHeader && styles.insideHeader,
    shouldWrap ? styles.wrap : styles.nowrap
  );

  if (overlay) {
    return (
      <div className={containerClassName}>
        {ghostItemsArray.map((_, index) => (
          <div
            className={styles.slot}
            style={{
              flexBasis: slotPercentageSize && `${slotPercentageSize}%`,
            }}
            key={index}
          >
            <GhostTile />
          </div>
        ))}
        <ClosedOverlay {...overlay} />
      </div>
    );
  }

  const createSlot = (
    position: number,
    outfit: QuicksilverTileOutfitType,
    slot?: GameTileProps,
    tileSize?: 'small' | 'large'
  ) => (
    <ModularGridGameTile
      data-testid="modular-grid-tile"
      game={slot}
      onClick={handleClick(position, onClick)}
      onInfoClick={handleClick(position, onInfoClick)}
      outfit={outfit}
      gameDetailsInfo={gameDetailsInfo}
      gameInfoTooltip={gameInfoTooltip}
      isLazy
      isAnchor
      categoryAttributes={categoryAttributes}
      renderExtendedInfo={renderExtendedInfo}
      tileSize={tileSize}
    />
  );

  return (
    <div className={containerClassName} data-testid="grid-grid">
      {isJackpotLarge && (
        <div
          className={cx(
            selectProperStyles(
              {
                small: styles.slotBigSmallForced,
                medium: styles.slotBigMediumForced,
                default: [styles.slotBigSmall, styles.slotBigMedium],
              },
              size
            )
          )}
          data-testid={`1-slot`}
          key={1}
        >
          {createSlot(1 + initialIndex, MEDIUM_OUTFIT, gameList[0], 'large')}
        </div>
      )}
      <div
        className={cx(
          type === 'jackpotNine' &&
            selectProperStyles(
              {
                small: styles.rowJackpotSmallForced,
                medium: styles.rowJackpotMediumForced,
                default: [styles.rowJackpotSmall, styles.rowJackpotMedium],
              },
              size
            ),
          {
            [styles.rowGrid]: type === 'grid' || type === 'collection',
            [styles.rowJackpot]: type === 'jackpotNine',
          }
        )}
      >
        {preparedGameList.map((item: GameTileProps, index: number) => (
          <div
            style={
              percentageWidth !== 100 || !gamesLimit
                ? {
                    flexBasis: slotPercentageSize && `${slotPercentageSize}%`,
                  }
                : {}
            }
            className={cx(
              type === 'jackpotNine' &&
                selectProperStyles(
                  {
                    small: styles.slotJackpotSmallForced,
                    medium: styles.slotJackpotMediumForced,
                    default: [
                      styles.slotJackpotSmall,
                      styles.slotJackpotMedium,
                    ],
                  },
                  size
                ),
              type === 'grid' &&
                selectProperStyles(
                  {
                    small: styles.slotGridSmallForced,
                    medium: styles.slotGridMediumForced,
                    default: [styles.slotGridSmall, styles.slotGridMedium],
                  },
                  size
                ),
              type === 'collection' &&
                selectProperStyles(
                  {
                    small: styles.slotCollectionSmallForced,
                    medium: styles.slotCollectionMediumForced,
                    default: [
                      styles.slotCollectionSmall,
                      styles.slotCollectionMedium,
                    ],
                  },
                  size
                ),
              styles.slot
            )}
            data-testid={`${index + gamePosition}-slot`}
            key={item?.id || index}
          >
            {createSlot(
              index + gamePosition + initialIndex,
              MEDIUM_OUTFIT,
              item
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
export default Grid;
